/**
 * ProjectSwitcher Component
 * Dropdown for quickly switching between projects
 * Shows current project, recent projects, and search
 *
 * FIXED (Dec 12, 2025): Click handling - removed asChild/Link combination
 * that was breaking cmdk's onSelect. Now uses onSelect with navigate() directly.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, ChevronsUpDown, FolderKanban, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useProjects } from '@/hooks/useProjects';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';

const RECENT_PROJECTS_KEY = 'corbel_recent_projects';
const MAX_RECENT_PROJECTS = 3;

/**
 * Save project to recent history
 */
const saveRecentProject = (projectId: string) => {
  try {
    const recent = JSON.parse(
      localStorage.getItem(RECENT_PROJECTS_KEY) || '[]'
    ) as string[];

    // Remove if already exists
    const filtered = recent.filter((id) => id !== projectId);

    // Add to front
    filtered.unshift(projectId);

    // Keep only max recent
    const updated = filtered.slice(0, MAX_RECENT_PROJECTS);

    localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Failed to save recent project:', error);
  }
};

/**
 * Get recent project IDs
 */
const getRecentProjectIds = (): string[] => {
  try {
    return JSON.parse(
      localStorage.getItem(RECENT_PROJECTS_KEY) || '[]'
    ) as string[];
  } catch {
    return [];
  }
};

export const ProjectSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { projects } = useProjects();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const currentProjectId = params.projectId;

  /**
   * Get current project
   */
  const currentProject = useMemo(() => {
    return projects.find((p) => p.id === currentProjectId);
  }, [projects, currentProjectId]);

  /**
   * Get recent projects (excluding current)
   */
  const recentProjects = useMemo(() => {
    const recentIds = getRecentProjectIds();
    return recentIds
      .map((id) => projects.find((p) => p.id === id))
      .filter((p) => p && p.id !== currentProjectId)
      .slice(0, MAX_RECENT_PROJECTS) as typeof projects;
  }, [projects, currentProjectId]);

  /**
   * Get all other projects (not current, not recent)
   */
  const otherProjects = useMemo(() => {
    const recentIds = getRecentProjectIds();
    return projects.filter(
      (p) => p.id !== currentProjectId && !recentIds.includes(p.id)
    );
  }, [projects, currentProjectId]);

  /**
   * Filter projects by search
   */
  const filteredProjects = useMemo(() => {
    if (!search) return projects;

    const lowerSearch = search.toLowerCase();
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(lowerSearch) ||
        p.companyName.toLowerCase().includes(lowerSearch) ||
        p.industry.toLowerCase().includes(lowerSearch)
    );
  }, [projects, search]);

  /**
   * Handle project selection
   */
  const handleSelectProject = (projectId: string) => {
    console.log('[ProjectSwitcher] handleSelectProject called for:', projectId);
    saveRecentProject(projectId);
    setOpen(false);
    setSearch('');
    // Use a small timeout to ensure the UI closes before navigation
    setTimeout(() => {
      console.log('[ProjectSwitcher] Navigating to project:', projectId);
      navigate(`/project/${projectId}`);
    }, 10);
  };

  /**
   * Handle create new project
   * Navigate to dashboard with query param to trigger create dialog
   */
  const handleCreateNew = () => {
    setOpen(false);
    setSearch('');
    // Navigate to dashboard with query param to open create dialog
    navigate('/dashboard?create=true');
  };

  /**
   * Get stage badge color
   */
  /**
   * Get stage badge color
   */
  const getStageBadge = (stage: string) => {
    const colors: Record<string, string> = {
      screening: 'bg-blue-500/10 text-blue-500',
      diligence: 'bg-amber-500/10 text-amber-500',
      ic: 'bg-violet-500/10 text-violet-500',
      closed: 'bg-emerald-500/10 text-emerald-500',
    };

    return colors[stage] || 'bg-muted text-muted-foreground';
  };

  if (!currentProject && projects.length === 0) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Select project"
          className={cn(
            'w-[42vw] min-w-[160px] max-w-[240px] justify-between',
            !currentProject && 'text-muted-foreground'
          )}
        >
          <div className="flex items-center gap-2 overflow-hidden">
            <FolderKanban className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <span className="truncate">
              {currentProject?.name || 'Select project'}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-1rem)] max-w-[340px] p-0 shadow-2xl border-border/50 z-50" align="end">
        <Command shouldFilter={false} className="pointer-events-auto opacity-100!">
          <CommandInput
            placeholder="Search projects..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No project found.</CommandEmpty>

            {/* Current Project */}
            {currentProject && !search && (
              <>
                <CommandGroup heading="Current Project">
                  <CommandItem
                    key={currentProject.id}
                    value={currentProject.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectProject(currentProject.id);
                    }}
                    className="flex items-center gap-2 py-3 px-4 w-full cursor-pointer hover:bg-accent hover:text-accent-foreground group !opacity-100 !pointer-events-auto !visible"
                  >
                    <Check className="h-4 w-4 text-primary" />
                    <FolderKanban className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{currentProject.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {currentProject.companyName}
                      </p>
                    </div>
                    <Badge
                      variant="secondary"
                      className={cn(
                        'text-xs',
                        getStageBadge(currentProject.stage)
                      )}
                    >
                      {currentProject.stage === 'ic' ? 'IC' : currentProject.stage}
                    </Badge>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            {/* Recent Projects */}
            {!search && recentProjects.length > 0 && (
              <>
                <CommandGroup heading="Recent">
                  {recentProjects.map((project) => (
                    <CommandItem
                      key={project.id}
                      value={project.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectProject(project.id);
                      }}
                      className="flex items-center gap-2 py-3 px-4 w-full cursor-pointer hover:bg-accent hover:text-accent-foreground group !opacity-100 !pointer-events-auto !visible"
                    >
                      <div className="w-4" />
                      <FolderKanban className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{project.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {project.companyName}
                        </p>
                      </div>
                      <Badge
                        variant="secondary"
                        className={cn('text-xs', getStageBadge(project.stage))}
                      >
                        {project.stage === 'ic' ? 'IC' : project.stage}
                      </Badge>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            {/* All Projects or Filtered Projects */}
            <CommandGroup heading={search ? 'Search Results' : 'All Projects'}>
              {(search ? filteredProjects : otherProjects).map((project) => (
                <CommandItem
                  key={project.id}
                  value={project.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectProject(project.id);
                  }}
                  className="flex items-center gap-2 py-3 px-4 w-full cursor-pointer hover:bg-accent hover:text-accent-foreground group !opacity-100 !pointer-events-auto !visible"
                >
                  {project.id === currentProjectId ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : (
                    <div className="w-4" />
                  )}
                  <FolderKanban className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{project.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {project.companyName}
                    </p>
                  </div>
                  <Badge
                    variant="secondary"
                    className={cn('text-xs', getStageBadge(project.stage))}
                  >
                    {project.stage === 'ic' ? 'IC' : project.stage}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>

            {/* Create New Project */}
            <CommandSeparator className="bg-border" />
            <CommandGroup>
              <CommandItem
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCreateNew();
                }}
                className="flex items-center gap-2 py-3 px-4 text-primary cursor-pointer hover:bg-accent hover:text-foreground !opacity-100 !pointer-events-auto !visible"
              >
                <Plus className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-foreground">Create New Project</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
